/**
 * Export Utilities for CSV and PDF generation
 * Provides functions to export data in various formats
 */

/**
 * Export data to CSV and trigger download
 * @param {Array<Object>} data - Array of objects to export
 * @param {string} filename - Name for the downloaded file (without extension)
 * @param {Object} options - Optional configuration
 * @param {Array<string>} options.columns - Specific columns to include (in order)
 * @param {Object} options.headers - Custom header labels { fieldName: 'Display Label' }
 */
export const exportToCSV = (data, filename, options = {}) => {
  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }

  const { columns, headers = {} } = options;

  // Determine columns to use
  const cols = columns || Object.keys(data[0]);

  // Build header row with custom labels
  const headerRow = cols.map(col => headers[col] || formatHeader(col));

  // Build data rows
  const dataRows = data.map(row => {
    return cols.map(col => {
      const value = row[col];
      return formatCSVValue(value);
    });
  });

  // Combine header and data
  const csvContent = [
    headerRow.join(','),
    ...dataRows.map(row => row.join(','))
  ].join('\n');

  // Create and trigger download
  downloadFile(csvContent, `${filename}.csv`, 'text/csv;charset=utf-8;');
};

/**
 * Export data to CSV with predefined report formats
 */
export const exportReportToCSV = (instances, filename = 'earnings-report') => {
  const data = instances.map(instance => ({
    job_name: instance?.job?.job_name || '',
    contractor: instance?.contractor?.full_name || '',
    scheduled_date: instance?.scheduled_date || '',
    clock_in: formatDateTime(instance?.timesheet?.[0]?.clock_in_time || instance?.timesheet?.clock_in_time),
    clock_out: formatDateTime(instance?.timesheet?.[0]?.clock_out_time || instance?.timesheet?.clock_out_time),
    total_hours: instance?.timesheet?.[0]?.total_hours || instance?.timesheet?.total_hours || 0,
    amount_earned: instance?.timesheet?.[0]?.amount_earned || instance?.timesheet?.amount_earned || 0,
    status: instance?.status || '',
    // Prefer job_site.address, fallback to location_address for legacy data
    location: instance?.job?.job_site?.address || instance?.job?.location_address || ''
  }));

  const headers = {
    job_name: 'Job Name',
    contractor: 'Contractor',
    scheduled_date: 'Date',
    clock_in: 'Clock In',
    clock_out: 'Clock Out',
    total_hours: 'Hours',
    amount_earned: 'Amount ($)',
    status: 'Status',
    location: 'Location'
  };

  exportToCSV(data, filename, { headers });
};

/**
 * Export contractor earnings summary to CSV
 */
export const exportContractorEarningsCSV = (contractors, dateRange, filename = 'contractor-earnings') => {
  const data = contractors.map(c => ({
    name: c.full_name || c.name || '',
    email: c.email || '',
    total_jobs: c.total_jobs || 0,
    total_hours: (c.total_hours || 0).toFixed(2),
    total_earnings: (c.total_earnings || 0).toFixed(2),
    approved_earnings: (c.approved_earnings || 0).toFixed(2),
    pending_earnings: (c.pending_earnings || 0).toFixed(2)
  }));

  const headers = {
    name: 'Contractor Name',
    email: 'Email',
    total_jobs: 'Total Jobs',
    total_hours: 'Total Hours',
    total_earnings: 'Total Earnings ($)',
    approved_earnings: 'Approved ($)',
    pending_earnings: 'Pending ($)'
  };

  const fname = dateRange
    ? `${filename}_${dateRange.start}_to_${dateRange.end}`
    : filename;

  exportToCSV(data, fname, { headers });
};

/**
 * Export job instances to CSV
 */
export const exportJobsToCSV = (jobs, filename = 'jobs-export') => {
  const data = jobs.map(job => ({
    job_id: job.job_display_id || job.id?.slice(-8) || '',
    job_name: job.job_name || job.title || '',
    // Client info (new structure)
    client: job.client?.client_name || '',
    // Job site info (new structure, fallback to legacy service_receiver)
    job_site: job.job_site?.site_name || job.service_receiver?.receiver_name || job.service_receiver?.name || '',
    contractor: job.contractor?.full_name || 'Unassigned',
    scheduled_date: job.scheduled_date || '',
    scheduled_time: job.scheduled_time || '',
    status: job.status || '',
    payment_type: job.payment_type || '',
    payment_amount: job.payment_amount || 0,
    // Prefer job_site.address, fallback to location_address for legacy data
    location: job.job_site?.address || job.location_address || ''
  }));

  const headers = {
    job_id: 'Job ID',
    job_name: 'Job Name',
    client: 'Client',
    job_site: 'Job Site',
    contractor: 'Contractor',
    scheduled_date: 'Date',
    scheduled_time: 'Time',
    status: 'Status',
    payment_type: 'Pay Type',
    payment_amount: 'Pay Amount ($)',
    location: 'Location'
  };

  exportToCSV(data, filename, { headers });
};

/**
 * Export timesheets to CSV
 */
export const exportTimesheetsToCSV = (timesheets, filename = 'timesheets-export') => {
  const data = timesheets.map(ts => ({
    date: ts.date || formatDate(ts.clock_in_time),
    contractor: ts.contractor?.full_name || ts.contractor_name || '',
    job_name: ts.job_name || ts.job?.job_name || '',
    clock_in: formatDateTime(ts.clock_in_time),
    clock_out: formatDateTime(ts.clock_out_time),
    break_minutes: ts.break_minutes || 0,
    total_hours: (ts.total_hours || 0).toFixed(2),
    pay_rate: ts.pay_rate || 0,
    amount_earned: (ts.amount_earned || 0).toFixed(2),
    status: ts.status || ''
  }));

  const headers = {
    date: 'Date',
    contractor: 'Contractor',
    job_name: 'Job',
    clock_in: 'Clock In',
    clock_out: 'Clock Out',
    break_minutes: 'Break (mins)',
    total_hours: 'Hours',
    pay_rate: 'Rate ($/hr)',
    amount_earned: 'Earnings ($)',
    status: 'Status'
  };

  exportToCSV(data, filename, { headers });
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Format a field name to a readable header
 */
const formatHeader = (fieldName) => {
  return fieldName
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

/**
 * Format a value for CSV (handle quotes, nulls, etc.)
 */
const formatCSVValue = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'object') {
    // Handle nested objects or arrays
    value = JSON.stringify(value);
  }

  // Convert to string
  const str = String(value);

  // Escape quotes and wrap in quotes if contains comma, newline, or quote
  if (str.includes(',') || str.includes('\n') || str.includes('"')) {
    return `"${str.replace(/"/g, '""')}"`;
  }

  return str;
};

/**
 * Format date for display
 */
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-AU');
  } catch {
    return dateStr;
  }
};

/**
 * Format datetime for display
 */
const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    return date.toLocaleString('en-AU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return dateStr;
  }
};

/**
 * Trigger file download
 */
const downloadFile = (content, filename, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Get current date string for filenames
 */
export const getDateFilename = (prefix = 'export') => {
  const now = new Date();
  const dateStr = now.toISOString().split('T')[0];
  return `${prefix}_${dateStr}`;
};

export default {
  exportToCSV,
  exportReportToCSV,
  exportContractorEarningsCSV,
  exportJobsToCSV,
  exportTimesheetsToCSV,
  getDateFilename
};
